"use client";

import { memo, useEffect, useMemo, useRef } from "react";
import { formatTranscriptLines } from "../../utils/formatTranscriptLines";

/** Match SOAP note body text ({@link InlineEditableLine} / EnhancedSoapNoteDisplay). */
const TRANSCRIPT_LINE_CLASS =
  "text-lg text-gray-700 leading-relaxed max-w-none";
const TRANSCRIPT_LIVE_CLASS =
  "text-lg font-medium text-gray-900 leading-relaxed max-w-none";
const TRANSCRIPT_PLACEHOLDER_CLASS = "text-lg text-gray-600 leading-relaxed";

function LiveTranscriptionLyricsImpl({
  transcript,
  liveTranscript,
  isRecording,
  /** Light chart styling (SOAP-like). Dark prompter only when false during live capture. */
  simplified = true,
  isUploadStreaming = false,
}) {
  const lines = useMemo(() => {
    const t = formatTranscriptLines(transcript || "");
    if (!t.trim()) return [];
    return t.split(/\n/).filter((line) => line.trim() !== "");
  }, [transcript]);

  const scrollRef = useRef(null);
  const followLiveText = isRecording || isUploadStreaming;

  useEffect(() => {
    if (!followLiveText) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [lines.length, followLiveText]);

  useEffect(() => {
    if (!followLiveText) return;
    const el = scrollRef.current;
    if (!el || !liveTranscript?.trim()) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
  }, [liveTranscript, followLiveText]);

  const hasBody = lines.length > 0 || (liveTranscript && liveTranscript.trim());
  const streamActive = followLiveText;

  const shellClass = simplified
    ? "min-h-[8rem] max-h-[60vh] overflow-y-auto overflow-x-hidden [overflow-anchor:none]"
    : "rounded-2xl bg-gradient-to-b from-neutral-900 via-neutral-900 to-neutral-950 border border-neutral-600/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] min-h-[min(18rem,42vh)] max-h-[min(38rem,68vh)] overflow-y-auto overflow-x-hidden px-5 py-8 md:px-10 md:py-10 [overflow-anchor:none]";

  return (
    <div
      ref={scrollRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
      className={shellClass}
    >
      <div
        className={
          simplified
            ? "space-y-3 text-left"
            : "mx-auto max-w-4xl space-y-4 text-center md:text-left"
        }
      >
        {!hasBody && streamActive ? (
          <p className={simplified ? TRANSCRIPT_PLACEHOLDER_CLASS : "text-base md:text-lg text-neutral-300 leading-relaxed"}>
            {simplified
              ? "Listening… text will appear here as you speak."
              : isUploadStreaming
                ? "Transcribing your file… text will stream in as it’s ready."
                : "Listening… recognized text will appear here line by line."}
          </p>
        ) : null}

        {lines.map((line, i) => {
          const isNearCurrent = i >= lines.length - 3;
          const lineClass = simplified
            ? `${TRANSCRIPT_LINE_CLASS}${
                isNearCurrent && liveTranscript?.trim() ? " text-gray-500" : ""
              }`
            : `text-base md:text-lg leading-[1.65] tracking-wide text-balance transition-colors duration-300 ${
                isNearCurrent && liveTranscript?.trim()
                  ? "text-neutral-400"
                  : "text-neutral-200"
              }`;
          return (
            <p key={`tl-${i}`} className={lineClass}>
              {line}
            </p>
          );
        })}

        {liveTranscript && liveTranscript.trim() ? (
          <div
            className={
              simplified
                ? "pt-3 mt-2 border-t border-gray-200 md:mt-0 md:border-t-0 md:border-l-4 md:border-blue-500 md:pl-5 md:pt-0 md:text-left"
                : "pt-4 mt-2 border-t border-white/10 md:mt-0 md:border-t-0 md:border-l-4 md:border-emerald-400/70 md:pl-6 md:pt-0 md:text-left"
            }
          >
            <p
              className={
                simplified
                  ? "text-sm font-semibold uppercase tracking-wider text-blue-700 mb-2"
                  : "text-sm font-semibold uppercase tracking-wider text-emerald-400/90 mb-2"
              }
            >
              {simplified ? "Current line" : "Live"}
            </p>
            <p className={simplified ? TRANSCRIPT_LIVE_CLASS : "text-base md:text-lg font-semibold text-white leading-[1.65] text-balance"}>
              {liveTranscript.trim()}
            </p>
          </div>
        ) : null}

        {/* Omit “waiting” between phrase finals — it flickered and felt like lost context. */}
      </div>
    </div>
  );
}

export const LiveTranscriptionLyrics = memo(LiveTranscriptionLyricsImpl);
